import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleNotch, faCopy, faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'

import formatNumber from 'src/utils/formatNumber'

import PageHeader from '../PageHeader'

// Last step in the crypto flow. We've called crypto_donations#create on
// the previous step (Contact's submit), so `crypto.stepTwo` holds the
// freshly-generated wallet address + network + optional destination tag.
// The donor scans / copies the address and sends crypto from their
// wallet; we poll #status every 10s to detect the inbound transaction,
// then hand off to ThankYou via `finished(...)`.
//
// The address is good for one hour — past that, we stop polling and
// invite the donor to retry.

const ADDRESS_LIFETIME_MS = 60 * 60 * 1000

const CryptoInfo = ({
  i18n, crypto, cryptoProps, donation, partnerItem, finished,
}) => {
  const { state, stepTwo } = crypto
  const [ received, setReceived ] = useState(null)
  const expiresAt = useRef(Date.now() + ADDRESS_LIFETIME_MS)
  const [ timeLeft, setTimeLeft ] = useState(ADDRESS_LIFETIME_MS)

  // Countdown ticker. Stops on receipt (donor's quote is locked) or
  // expiry (interval clears itself when timeLeft hits 0).
  useEffect(() => {
    if (received) return
    const tick = () => {
      const remaining = expiresAt.current - Date.now()
      setTimeLeft(remaining)
      if (remaining <= 0) clearInterval(interval)
    }
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [ received ])

  // Status polling. Sets `received` from the serialized order once the
  // on-chain transaction is observed; the next effect below picks up
  // that transition and schedules the ThankYou hand-off.
  //
  // The serializer's currency payload includes the crypto "%n %c"
  // format hint that I18n.formatCurrency needs — without it the
  // donor's i18n.currency.format (e.g. "%u%n" for USD) would render
  // the amount with no currency indicator at all.
  useEffect(() => {
    const check = async () => {
      // Stop polling once the address has expired. The effect deps
      // are [] so re-rendering on `expired` flipping doesn't tear this
      // down; we read expiresAt.current at tick time instead. Without
      // this guard a donor who opens crypto and walks away would keep
      // hitting statusUrl every 10s for as long as the panel stayed
      // mounted, well past the 1-hour address lifetime.
      if (Date.now() >= expiresAt.current) {
        clearInterval(interval)
        return
      }
      try {
        const url = `${cryptoProps.statusUrl}?address=${encodeURIComponent(stepTwo.address)}`
        const response = await fetch(url, { headers: { Accept: 'application/json' } })
        if (!response.ok) return

        const { order, token } = await response.json()
        if (order) {
          setReceived({ ...order, token })
          clearInterval(interval)
        }
      } catch (error) {
        console.error(error)
      }
    }
    const interval = setInterval(check, 10000)
    return () => clearInterval(interval)
  }, [])

  // Hand off to ThankYou 3s after receipt so the donor sees the
  // success state on this panel before navigation. Cleanup cancels
  // the pending timeout if the donor backs out / unmounts in the
  // brief window between observation and navigation.
  useEffect(() => {
    if (!received) return
    const timeout = setTimeout(() => finished({
      ...donation.state,
      ...received,
      partnerItem,
    }), 3000)
    return () => clearTimeout(timeout)
  }, [ received ])

  const expired = timeLeft <= 0
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)
  const lpad = n => n.toString().padStart(2, '0')

  const copy = id => {
    const el = document.getElementById(id)
    if (!el) return
    navigator.clipboard?.writeText(el.textContent)
  }

  const heading = received
    ? i18n.t('crypto_info.heading.received')
    : i18n.t('crypto_info.heading.pending')

  const subtitle = received
    ? i18n.t('crypto_info.received.body', {
        amount: formatNumber(received.amount),
        currency: received.currency.iso_code,
      })
    : expired
    ? i18n.t('crypto_info.expired.body')
    : i18n.t('crypto_info.pending.body', {
        amount: formatNumber(state.crypto_amount.toString()),
        currency: state.crypto_currency,
      })

  return (
    <div className="space-y-4">
      <PageHeader heading={heading} subtitle={subtitle} />

      <p className="text-center">
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?${new URLSearchParams({
            data: stepTwo.address,
            size: '144x144',
          })}`}
          alt={stepTwo.address}
          className="inline-block"
        />
      </p>

      <div>
        <h3 className="text-sm font-medium text-gray-700 mb-1">
          {i18n.t('crypto_info.address')}
        </h3>
        <div className="flex bg-gray-100 rounded-(--input-radius-lg) border-(length:--input-border-width) border-(--input-border)">
          <pre
            id="crypto-address"
            className={`flex-1 min-w-0 py-2 px-3 overflow-x-auto text-sm ${expired ? 'line-through' : ''}`}
          >{stepTwo.address}</pre>
          {!expired && (
            <button
              type="button"
              onClick={() => copy('crypto-address')}
              className="btn btn-sm btn-primary m-0.5"
              aria-label={i18n.t('crypto_info.copy')}
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          )}
        </div>
      </div>

      {stepTwo.network_name && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-1">
            {i18n.t('crypto_info.network')}
          </h3>
          <p className="bg-gray-100 rounded-(--input-radius-lg) border-(length:--input-border-width) border-(--input-border) py-2 px-3 text-sm font-semibold">
            {stepTwo.network_name}
          </p>
        </div>
      )}

      {stepTwo.destination_tag && (
        <div>
          <h3 className="text-sm font-medium text-gray-700 mb-1">
            {i18n.t('crypto_info.destination_tag')}
          </h3>
          <div className="flex bg-gray-100 rounded-(--input-radius-lg) border-(length:--input-border-width) border-(--input-border)">
            <pre
              id="crypto-destination-tag"
              className={`flex-1 min-w-0 py-2 px-3 overflow-x-auto text-sm ${expired ? 'line-through' : ''}`}
            >{stepTwo.destination_tag}</pre>
            {!expired && (
              <button
                type="button"
                onClick={() => copy('crypto-destination-tag')}
                className="btn btn-sm btn-primary m-0.5"
                aria-label={i18n.t('crypto_info.copy')}
              >
                <FontAwesomeIcon icon={faCopy} />
              </button>
            )}
          </div>
        </div>
      )}

      {stepTwo.network_warning && stepTwo.network_name && !received && (
        <p className="rounded-md border border-warning-300 bg-warning-50 text-warning-800 p-3 text-sm">
          <FontAwesomeIcon icon={faExclamationTriangle} className="me-3" />
          <span>{i18n.t('crypto_info.network_warning', { network: stepTwo.network_name })}</span>
        </p>
      )}

      <p className="font-semibold">
        {received ? (
          <span className="text-success-600">
            {i18n.t('crypto_info.status.received')}
          </span>
        ) : expired ? (
          <span className="text-gray-500">
            {i18n.t('crypto_info.status.expired')}
          </span>
        ) : (
          <>
            <span className="text-info-600 flex items-center gap-2">
              {i18n.t('crypto_info.status.pending')}
              <FontAwesomeIcon icon={faCircleNotch} spin />
            </span>
            <small className="block font-normal text-gray-500 mt-1">
              {i18n.t('crypto_info.status.update_note')}
            </small>
          </>
        )}
      </p>

      {received ? (
        <p>{i18n.t('crypto_info.received.confirmation')}</p>
      ) : expired ? (
        <p>{i18n.t('crypto_info.expired.retry')}</p>
      ) : (
        <p>
          {i18n.t('crypto_info.expiry.body', {
            time: `${lpad(minutes)}:${lpad(seconds)}`,
          })}
        </p>
      )}
    </div>
  )
}

export default CryptoInfo
